import { useState, useRef } from "react";
import axios from "axios";
import { API, getAuthToken } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Upload, FileText, AlertTriangle } from "lucide-react";
import { extractErrorMessage } from "@/lib/utils";

const EWAY_THRESHOLD = 50000; // ₹ — mirrors backend EWAY_BILL_THRESHOLD

export default function InvoiceUploadModal({ quotation, open, onClose, onSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [plannedPickupDate, setPlannedPickupDate] = useState("");
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [ewayFile, setEwayFile] = useState(null);
  const invoiceInputRef = useRef(null);
  const ewayInputRef = useRef(null);

  const invoiceValue = Number(quotation?.total_invoice_value) || 0;
  const ewayRequired = invoiceValue > EWAY_THRESHOLD;

  const reset = () => {
    setInvoiceNumber("");
    setInvoiceDate("");
    setPlannedPickupDate("");
    setInvoiceFile(null);
    setEwayFile(null);
  };

  const handleUpload = async () => {
    if (!invoiceNumber.trim()) { toast.error("Please enter invoice number"); return; }
    if (!invoiceDate) { toast.error("Please enter invoice date"); return; }
    if (!plannedPickupDate) { toast.error("Please select a planned pickup date"); return; }
    if (!invoiceFile) { toast.error("Please select an invoice file"); return; }
    if (ewayRequired && !ewayFile) { toast.error(`E-Way Bill is mandatory for invoices above ₹${EWAY_THRESHOLD.toLocaleString("en-IN")}`); return; }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", invoiceFile);
      if (ewayFile) formData.append("eway_bill_file", ewayFile);

      await axios.post(
        `${API}/quotations/${quotation.id}/upload-invoice?invoice_number=${encodeURIComponent(invoiceNumber)}&invoice_date=${invoiceDate}&planned_pickup_date=${plannedPickupDate}`,
        formData,
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );

      toast.success("Invoice uploaded. Quotation locked & scheduled for dispatch.");
      reset();
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Error uploading invoice"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle data-testid="invoice-upload-title">Upload Invoice</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex justify-between text-sm bg-slate-50 rounded p-2.5">
            <span className="text-muted-foreground">Quotation ID:</span>
            <span className="font-mono font-semibold">{quotation?.quotation_id}</span>
          </div>
          <div className="flex justify-between text-sm bg-slate-50 rounded p-2.5">
            <span className="text-muted-foreground">Invoice Value:</span>
            <span className="font-mono font-semibold">₹{invoiceValue.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="invoice_number">Invoice Number *</Label>
            <Input id="invoice_number" type="text" value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="Enter invoice number" required
              data-testid="invoice-number-input" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="invoice_date">Invoice Date *</Label>
            <Input id="invoice_date" type="date" value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              required data-testid="invoice-date-input" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="planned_pickup_date">Planned Pickup Date *</Label>
            <Input id="planned_pickup_date" type="date" value={plannedPickupDate}
              onChange={(e) => setPlannedPickupDate(e.target.value)}
              required data-testid="planned-pickup-date-input" />
            <p className="text-[11px] text-muted-foreground">Schedules this quotation for dispatch. Sets the initial Final Pickup Date.</p>
          </div>

          <div className="space-y-1.5">
            <Label>Invoice File *</Label>
            <input ref={invoiceInputRef} type="file" accept=".pdf"
              onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)}
              className="hidden" />
            <Button type="button" variant="outline" className="w-full justify-start"
              onClick={() => invoiceInputRef.current?.click()}
              data-testid="select-invoice-file-button">
              <FileText className="h-4 w-4 mr-2 flex-shrink-0" />
              <span className="truncate">{invoiceFile ? invoiceFile.name : "Select Invoice PDF"}</span>
            </Button>
          </div>

          {ewayRequired && (
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                E-Way Bill *
                <span className="text-[10px] font-normal text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                  Required (invoice &gt; ₹50,000)
                </span>
              </Label>
              <input ref={ewayInputRef} type="file" accept=".pdf"
                onChange={(e) => setEwayFile(e.target.files?.[0] || null)}
                className="hidden" />
              <Button type="button" variant="outline" className="w-full justify-start"
                onClick={() => ewayInputRef.current?.click()}
                data-testid="select-eway-file-button">
                <FileText className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="truncate">{ewayFile ? ewayFile.name : "Select E-Way Bill PDF"}</span>
              </Button>
            </div>
          )}

          <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded text-xs">
            <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-amber-900">
              Uploading will <strong>lock this quotation</strong>. Edit &amp; Delete will be disabled; status → <strong>Invoice Uploaded</strong>.
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { reset(); onClose(); }} disabled={uploading}>Cancel</Button>
            <Button onClick={handleUpload}
              disabled={uploading || !invoiceFile || !invoiceDate || !plannedPickupDate || !invoiceNumber.trim() || (ewayRequired && !ewayFile)}
              className="bg-accent hover:bg-accent/90 text-white"
              data-testid="upload-invoice-button">
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
